import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Phone, FileText, CheckCircle2, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { regions, services } from '../data/mock';

const Zone = () => {
  const { slug } = useParams();
  const region = regions.find(r => r.slug === slug);

  if (!region) {
    return (
      <div className="min-h-screen pt-32 pb-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Zone introuvable</h1>
          <Link to="/">
            <Button>Retour à l'accueil</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-20 bg-gradient-to-br from-blue-900 to-blue-800 text-white overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-400/20 rounded-full blur-3xl"></div>
        <div className="container mx-auto px-4 relative z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-blue-200 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Retour à l'accueil
          </Link>
          
          <div className="max-w-4xl">
            <Badge className="mb-6 bg-cyan-500/20 text-cyan-200 hover:bg-cyan-500/30 border-0 text-base px-4 py-2">
              <MapPin className="w-4 h-4 mr-2" />
              Zone d'intervention
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight" style={{ fontFamily: '"Outfit", sans-serif', letterSpacing: '-0.5px' }}>
              Nettoyage par drone {region.name}
            </h1>
            <p className="text-xl text-blue-100 leading-relaxed" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
              PULVISTAR intervient rapidement dans toute la région {region.name} pour vos besoins de nettoyage professionnel par drone
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <Button size="lg" className="gap-2 bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = '/devis'}>
                <FileText className="w-5 h-5" />
                Demander un devis
              </Button>
              <Button size="lg" variant="outline" className="gap-2 border-white text-white hover:bg-white/10" onClick={() => window.location.href = 'tel:+33184180342'}>
                <Phone className="w-5 h-5" />
                Appeler
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services in Region */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-12 text-center" style={{ fontFamily: '"Outfit", sans-serif', letterSpacing: '-0.5px' }}>
            Nos services {region.name}
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <Card key={service.id} className="p-6 border-gray-200 hover:shadow-lg transition-all hover:-translate-y-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-lg flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-blue-900" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900" style={{ fontFamily: '"Outfit", sans-serif' }}>
                    {service.title}
                  </h3>
                </div>
                <p className="text-gray-600 mb-4" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
                  {service.description}
                </p>
                <Link to={`/services/${service.slug}`} className="text-cyan-600 font-medium text-sm inline-flex items-center gap-1 hover:gap-2 transition-all">
                  En savoir plus <ArrowLeft className="w-4 h-4 rotate-180" />
                </Link>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us in Region */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-12 text-center" style={{ fontFamily: '"Outfit", sans-serif', letterSpacing: '-0.5px' }}>
              Pourquoi choisir PULVISTAR {region.name} ?
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="p-6 border-gray-200 bg-white">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2" style={{ fontFamily: '"Outfit", sans-serif' }}>
                      Intervention rapide
                    </h3>
                    <p className="text-gray-600 text-sm" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
                      Déploiement en 48h dans toute la région {region.name}. Équipes mobiles et réactives.
                    </p>
                  </div>
                </div>
              </Card>
              
              <Card className="p-6 border-gray-200 bg-white">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2" style={{ fontFamily: '"Outfit", sans-serif' }}>
                      Expertise locale
                    </h3>
                    <p className="text-gray-600 text-sm" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
                      Connaissance du territoire et des contraintes climatiques spécifiques de la région.
                    </p>
                  </div>
                </div>
              </Card>
              
              <Card className="p-6 border-gray-200 bg-white">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2" style={{ fontFamily: '"Outfit", sans-serif' }}>
                      Tarifs compétitifs
                    </h3>
                    <p className="text-gray-600 text-sm" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
                      Jusqu'à 40% moins cher que les méthodes traditionnelles avec échafaudage ou nacelle.
                    </p>
                  </div>
                </div>
              </Card>
              
              <Card className="p-6 border-gray-200 bg-white">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2" style={{ fontFamily: '"Outfit", sans-serif' }}>
                      Qualité garantie
                    </h3>
                    <p className="text-gray-600 text-sm" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
                      Assurance professionnelle, pilotes certifiés, matériel de pointe, contrôle qualité.
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-r from-cyan-50 to-blue-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: '"Outfit", sans-serif', letterSpacing: '-0.5px' }}>
            Prêt à démarrer votre projet {region.name} ?
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
            Contactez-nous pour obtenir un devis gratuit et détaillé sous 24h
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="gap-2 bg-blue-900 hover:bg-blue-800" onClick={() => window.location.href = '/devis'}>
              Obtenir un devis gratuit <FileText className="w-5 h-5" />
            </Button>
            <Button size="lg" variant="outline" className="gap-2" onClick={() => window.location.href = '/contact'}>
              Nous contacter
            </Button>
          </div>
        </div>
      </section>

      {/* Other Regions */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center" style={{ fontFamily: '"Outfit", sans-serif' }}>
            Nous intervenons également dans ces régions
          </h3>
          <div className="grid md:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {regions.filter(r => r.slug !== slug).map((otherRegion) => (
              <Link key={otherRegion.slug} to={`/zone/${otherRegion.slug}`}>
                <Card className="p-4 border-gray-200 hover:shadow-lg transition-all hover:-translate-y-1 text-center">
                  <MapPin className="w-6 h-6 text-cyan-500 mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-900" style={{ fontFamily: '"Inter", sans-serif' }}>
                    {otherRegion.name}
                  </p>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Zone;
